import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  plumbingOverview, listSuppliers, createSupplier, updateSupplier, deleteSupplier, discoverSuppliers,
  listTeams, createTeam, deleteTeam, listFieldEmployees, inviteFieldEmployee, removeFieldEmployee, apiError,
} from "@/lib/api";
import { StockOrders } from "@/components/StockOrders";
import { Jobs } from "@/components/Jobs";
import { MonthlyReports } from "@/components/MonthlyReports";
import { PageHeader, Card, Loading, ConfirmDelete } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wrench, Users, MapPin, FileText, HardHat, Plus, Trash2, Sparkles, Search, Package, ClipboardList, BarChart3, Pencil } from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: Wrench },
  { id: "jobs", label: "Jobs", icon: ClipboardList },
  { id: "stock", label: "Stock Orders", icon: Package },
  { id: "reports", label: "Monthly Reports", icon: BarChart3 },
  { id: "suppliers", label: "Suppliers", icon: MapPin },
  { id: "teams", label: "Teams", icon: Users },
  { id: "employees", label: "Field Employees", icon: HardHat },
];

export default function Plumbing() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);

  useEffect(() => { plumbingOverview().then(setOv).catch((e) => toast.error(apiError(e))); }, []);
  if (!ov) return <Loading />;

  if (!ov.entitled) {
    return (
      <div data-testid="plumbing-page">
        <PageHeader title="Plumbing Operations" subtitle="Field workforce & supplier material ordering" />
        <Card className="p-10 text-center max-w-xl mx-auto" data-testid="plumbing-upsell">
          <div className="w-14 h-14 rounded-2xl bg-[#1E7A6B]/12 flex items-center justify-center mx-auto text-[#1E7A6B]"><Sparkles className="w-7 h-7" /></div>
          <h3 className="font-display text-xl font-bold text-[#0B132B] mt-4">Plumbing Operations is available on the Business and Pro plans</h3>
          <p className="text-slate-500 text-sm mt-2">Manage unlimited field plumbers, teams, stock orders and supplier material ordering.</p>
          <Button onClick={() => nav("/billing")} className="mt-6 bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="plumbing-upgrade-btn">Upgrade Plan</Button>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="plumbing-page">
      <PageHeader title="Plumbing Operations" subtitle="Field workforce & supplier material ordering" />
      <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`plumb-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#1E7A6B] text-[#0B132B]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview ov={ov} setTab={setTab} />}
      {tab === "jobs" && <Jobs manage={true} />}
      {tab === "stock" && <StockOrders manage={true} />}
      {tab === "reports" && <MonthlyReports />}
      {tab === "suppliers" && <SuppliersTab />}
      {tab === "teams" && <TeamsTab />}
      {tab === "employees" && <EmployeesTab />}
    </div>
  );
}

function Overview({ ov, setTab }) {
  const cards = [
    { label: "Field Employees", value: ov.field_employees, icon: HardHat },
    { label: "Teams", value: ov.teams, icon: Users },
    { label: "Saved Suppliers", value: ov.supplier_count ?? 0, icon: MapPin },
    { label: "Plan", value: "Business / Pro", icon: FileText },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plumbing-overview">
      {cards.map((c) => (
        <Card key={c.label} className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><c.icon className="w-4 h-4" /> {c.label}</div>
          <div className="mt-2 text-2xl font-bold text-[#0B132B]">{c.value}</div>
        </Card>
      ))}
    </div>
  );
}

const CAT = { plumbing_supply: "Plumbing supply store", plumbing_wholesaler: "Plumbing wholesaler", general_hardware: "General hardware", building_materials: "Building materials", other: "Other" };
const emptySupplier = () => ({ name: "", category: "plumbing_supply", branch_name: "", salesperson_name: "", whatsapp_number: "", telephone: "", account_number: "", suburb: "", city: "", address: "", postcode: "", latitude: null, longitude: null, instructions: "", trading_hours: "", closing_days: "", preferred_ordering_method: "", fulfilment_preference: "", delivery_or_collection_point: "", minimum_order: "", payment_terms: "", website: "", materials_supplied: "" });
const dirUrl = (s) => (s.latitude && s.longitude) ? `https://www.google.com/maps/dir/?api=1&destination=${s.latitude},${s.longitude}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([s.name, s.suburb, s.city].filter(Boolean).join(" "))}`;

function SuppliersTab() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null); // supplier being added/edited
  const [saving, setSaving] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);

  const load = () => listSuppliers(q ? { q } : {}).then(setList).catch((e) => { setList([]); toast.error(apiError(e)); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => setForm(emptySupplier());
  const openEdit = (s) => setForm({ ...emptySupplier(), ...s });
  const save = async () => {
    if (!(form.name || "").trim()) return toast.error("Supplier name is required");
    setSaving(true);
    try {
      if (form.id) await updateSupplier(form.id, form);
      else await createSupplier(form);
      toast.success("Supplier saved"); setForm(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const saveDiscovered = async (r) => {
    try { await createSupplier({ ...emptySupplier(), name: r.name, category: r.category, address: r.address, suburb: r.suburb, city: r.city, postcode: r.postcode, latitude: r.latitude, longitude: r.longitude, website: r.website || "" });
      toast.success(`Saved ${r.name} — add the salesperson's WhatsApp number to order`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!list) return <Loading />;
  return (
    <div data-testid="suppliers-tab">
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9" placeholder="Search saved suppliers by name, suburb or town" data-testid="supplier-search" /></div>
        <Button variant="outline" onClick={load} data-testid="supplier-search-btn">Search</Button>
        <Button variant="outline" onClick={() => setDiscoverOpen(true)} data-testid="supplier-discover-btn"><MapPin className="w-4 h-4 mr-1" /> Find nearby</Button>
        <Button onClick={openNew} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="add-supplier-btn"><Plus className="w-4 h-4 mr-1" /> Add supplier</Button>
      </div>

      {list.length === 0 ? (
        <Card className="p-10 text-center text-slate-400" data-testid="suppliers-empty">
          <MapPin className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          No suppliers saved yet. Add a supplier manually or use <b>Find nearby</b> to discover plumbing &amp; hardware stores around you.
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="supplier-list">
          {list.map((s) => (
            <Card key={s.id} className="p-4" data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-[#0B132B] truncate">{s.name}</div>
                  <div className="text-xs text-[#1E7A6B] font-medium mt-0.5">{CAT[s.category] || "Supplier"}</div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`supplier-edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                  <ConfirmDelete onConfirm={async () => { await deleteSupplier(s.id); load(); toast.success("Supplier removed"); }} title="Remove supplier?" trigger={<Button variant="ghost" size="sm" className="text-rose-600" data-testid={`supplier-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                </div>
              </div>
              {s.branch_name && <div className="text-xs text-slate-500 mt-1">{s.branch_name}</div>}
              {(s.address || s.suburb || s.city) && <div className="text-xs text-slate-400 mt-1">{[s.address, s.suburb, s.city].filter(Boolean).join(", ")}</div>}
              {s.distance_km != null && <div className="text-xs text-[#1E7A6B] font-semibold mt-1">{s.distance_km} km away</div>}
              <div className="text-xs mt-2">{s.whatsapp_e164 ? <span className="text-emerald-600">WhatsApp +{s.whatsapp_e164}{s.salesperson_name ? ` · ${s.salesperson_name}` : ""}</span> : <span className="text-amber-600">No WhatsApp number — edit to add before ordering</span>}</div>
              {s.account_number && <div className="text-xs text-slate-400 mt-0.5">Account: {s.account_number}</div>}
              {(s.latitude || s.suburb) && <a href={dirUrl(s)} target="_blank" rel="noreferrer" className="text-xs text-[#1E7A6B] underline mt-1 inline-block">View directions</a>}
            </Card>
          ))}
        </div>
      )}

      <SupplierForm form={form} setForm={setForm} onSave={save} saving={saving} onClose={() => setForm(null)} />
      <DiscoverDialog open={discoverOpen} onClose={() => setDiscoverOpen(false)} onSave={saveDiscovered} />
    </div>
  );
}

function SupplierForm({ form, setForm, onSave, saving, onClose }) {
  if (!form) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <Dialog open={!!form} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="supplier-form-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Edit supplier" : "Add supplier"}</DialogTitle><DialogDescription>Save a supplier for future orders. Only the name is required — add the salesperson's WhatsApp number to send orders.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Supplier name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="supplier-name" /></div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="supplier-category"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CAT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Branch / store name</Label><Input value={form.branch_name} onChange={(e) => set("branch_name", e.target.value)} placeholder="e.g. Midrand" /></div>
            <div><Label>Salesperson name</Label><Input value={form.salesperson_name} onChange={(e) => set("salesperson_name", e.target.value)} /></div>
            <div><Label>Salesperson WhatsApp number</Label><Input value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="082 123 4567 or +27 82 123 4567" data-testid="supplier-whatsapp" /></div>
            <div><Label>Telephone (optional)</Label><Input value={form.telephone} onChange={(e) => set("telephone", e.target.value)} /></div>
            <div><Label>Supplier account number</Label><Input value={form.account_number} onChange={(e) => set("account_number", e.target.value)} placeholder="Your trade account no." data-testid="supplier-account" /></div>
            <div><Label>Website (optional)</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div><Label>Suburb</Label><Input value={form.suburb} onChange={(e) => set("suburb", e.target.value)} /></div>
            <div><Label>Town / city</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
            <div><Label>Postcode</Label><Input value={form.postcode} onChange={(e) => set("postcode", e.target.value)} /></div>
            <div><Label>Trading hours (optional)</Label><Input value={form.trading_hours} onChange={(e) => set("trading_hours", e.target.value)} placeholder="Mon–Fri 07:00–17:00" /></div>
            <div><Label>Closing days (optional)</Label><Input value={form.closing_days} onChange={(e) => set("closing_days", e.target.value)} placeholder="Sunday, public holidays" /></div>
            <div><Label>Preferred ordering method (optional)</Label><Input value={form.preferred_ordering_method} onChange={(e) => set("preferred_ordering_method", e.target.value)} placeholder="WhatsApp / phone" /></div>
            <div><Label>Collection or delivery (optional)</Label>
              <Select value={form.fulfilment_preference || "none"} onValueChange={(v) => set("fulfilment_preference", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="supplier-fulfil"><SelectValue placeholder="Not set" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not set</SelectItem><SelectItem value="collect">Collection</SelectItem><SelectItem value="deliver">Delivery</SelectItem><SelectItem value="either">Either</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Delivery / collection point (optional)</Label><Input value={form.delivery_or_collection_point} onChange={(e) => set("delivery_or_collection_point", e.target.value)} /></div>
            <div><Label>Minimum order (optional)</Label><Input value={form.minimum_order} onChange={(e) => set("minimum_order", e.target.value)} /></div>
          </div>
          <div><Label>Materials supplied (optional)</Label><Input value={form.materials_supplied} onChange={(e) => set("materials_supplied", e.target.value)} placeholder="pipes, fittings, valves, geysers, tools" /></div>
          <div><Label>Payment terms / account notes (optional)</Label><Textarea rows={2} value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} /></div>
          <div><Label>Ordering instructions (optional)</Label><Textarea rows={2} value={form.instructions} onChange={(e) => set("instructions", e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={onSave} disabled={saving} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="save-supplier-btn">{saving ? "Saving…" : "Save supplier"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiscoverDialog({ open, onClose, onSave }) {
  const [place, setPlace] = useState("");
  const [radius, setRadius] = useState("10");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async (params) => {
    setLoading(true); setData(null);
    try { const r = await discoverSuppliers({ ...params, radius_km: radius }); setData(r); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const byPlace = () => { if (!place.trim()) return toast.error("Enter a suburb, town or postcode"); run({ place }); };
  const byLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not supported — search by suburb instead");
    navigator.geolocation.getCurrentPosition(
      (p) => run({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => toast.error("Location unavailable — search by suburb instead"));
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="discover-dialog">
        <DialogHeader><DialogTitle>Find nearby suppliers</DialogTitle><DialogDescription>Discover real plumbing, hardware &amp; building-material stores near you using OpenStreetMap, then save the ones you use.</DialogDescription></DialogHeader>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={place} onChange={(e) => setPlace(e.target.value)} onKeyDown={(e) => e.key === "Enter" && byPlace()} placeholder="Suburb, town or postcode (Gauteng)" className="flex-1" data-testid="discover-place" />
          <Select value={radius} onValueChange={setRadius}>
            <SelectTrigger className="w-28" data-testid="discover-radius"><SelectValue /></SelectTrigger>
            <SelectContent>{["1", "5", "10", "20"].map((r) => <SelectItem key={r} value={r}>{r} km</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={byPlace} data-testid="discover-search-btn">Search</Button>
          <Button variant="outline" onClick={byLocation} data-testid="discover-location-btn"><MapPin className="w-4 h-4 mr-1" /> My location</Button>
        </div>
        <div className="mt-3">
          {loading && <Loading />}
          {!loading && data?.source_error && <p className="text-sm text-amber-600 p-4 text-center">Live nearby search is temporarily unavailable. Please try again, search by a different suburb, or add the supplier manually.</p>}
          {!loading && data && !data.source_error && data.results.length === 0 && <p className="text-sm text-slate-500 p-4 text-center">No suppliers found within {data.radius_km} km. Try a larger radius or a different area.</p>}
          {!loading && data?.results?.length > 0 && (
            <div className="space-y-2" data-testid="discover-results">
              {data.results.map((r, i) => (
                <div key={r.osm_id || i} className="flex items-center justify-between gap-2 border border-slate-100 rounded-lg p-3" data-testid={`discover-result-${i}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#0B132B] truncate">{r.name}</div>
                    <div className="text-xs text-slate-500">{CAT[r.category]} · {r.distance_km} km{[r.suburb, r.city].filter(Boolean).length ? ` · ${[r.suburb, r.city].filter(Boolean).join(", ")}` : ""}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <a href={dirUrl(r)} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm">Directions</Button></a>
                    <Button size="sm" className="bg-[#1E7A6B] hover:bg-[#186052] text-white" onClick={() => onSave(r)} data-testid={`discover-save-${i}`}>Save</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TeamsTab() {
  const [teams, setTeams] = useState([]);
  const [name, setName] = useState("");
  const load = () => listTeams().then(setTeams).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name.trim()) return; try { await createTeam({ name: name.trim() }); setName(""); load(); toast.success("Team created"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div data-testid="teams-tab">
      <div className="flex gap-2 mb-4 max-w-md">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New team name" data-testid="team-name-input" />
        <Button onClick={add} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="add-team-btn"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {teams.map((t) => (
          <Card key={t.id} className="p-4 flex items-center justify-between" data-testid={`team-card-${t.id}`}>
            <div><div className="font-semibold text-[#0B132B]">{t.name}</div><div className="text-xs text-slate-500">{t.member_count} member{t.member_count === 1 ? "" : "s"}{t.team_leader_name ? ` · Lead: ${t.team_leader_name}` : ""}</div></div>
            <ConfirmDelete onConfirm={async () => { await deleteTeam(t.id); load(); toast.success("Team deleted"); }} title="Delete team?" trigger={<Button variant="ghost" size="sm" className="text-rose-600"><Trash2 className="w-4 h-4" /></Button>} />
          </Card>
        ))}
        {teams.length === 0 && <p className="text-slate-400 text-sm">No teams yet.</p>}
      </div>
    </div>
  );
}

function EmployeesTab() {
  const [data, setData] = useState(null);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", job_title: "Plumber", employee_number: "", mobile: "", team_id: "none", is_team_leader: false });
  const load = () => { listFieldEmployees().then(setData).catch((e) => toast.error(apiError(e))); listTeams().then(setTeams).catch(() => {}); };
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;
  const submit = async () => {
    if (!form.email || !form.name) return toast.error("Name and email required");
    try {
      await inviteFieldEmployee({ ...form, team_id: form.team_id === "none" ? null : form.team_id });
      toast.success("Field employee invited"); setOpen(false);
      setForm({ email: "", name: "", job_title: "Plumber", employee_number: "", mobile: "", team_id: "none", is_team_leader: false });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const titles = ["Plumber", "Assistant Plumber", "Technician", "Apprentice", "Team Leader"];
  return (
    <div data-testid="employees-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">Field employees are unlimited on your plan and separate from admin team members.</p>
        <Button onClick={() => setOpen(true)} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="invite-field-btn"><Plus className="w-4 h-4 mr-2" /> Add Field Employee</Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="field-employees-table">
          <thead><tr className="text-left text-slate-400 text-xs uppercase border-b border-slate-100">
            <th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Job Title</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {data.employees.map((e) => (
              <tr key={e.id} className="border-b border-slate-50" data-testid={`field-row-${e.id}`}>
                <td className="px-5 py-4 font-medium text-[#0B132B]">{e.name} {e.is_team_leader && <span className="text-xs text-[#8a6d0f]">(Lead)</span>}</td>
                <td className="px-5 py-4 text-slate-600">{e.email}</td>
                <td className="px-5 py-4 text-slate-600">{e.job_title}</td>
                <td className="px-5 py-4">{e.joined ? <span className="text-emerald-600 text-xs">Active</span> : <span className="text-amber-600 text-xs">Invited</span>}</td>
                <td className="px-5 py-4 text-right"><ConfirmDelete onConfirm={async () => { await removeFieldEmployee(e.id); load(); toast.success("Removed"); }} title="Remove field employee?" trigger={<Button variant="ghost" size="sm" className="text-rose-600"><Trash2 className="w-4 h-4" /></Button>} /></td>
              </tr>
            ))}
            {data.pending.map((p) => (
              <tr key={p.id} className="border-b border-slate-50 bg-amber-50/40">
                <td className="px-5 py-4 font-medium text-[#0B132B]">{p.name}</td><td className="px-5 py-4 text-slate-600">{p.email}</td>
                <td className="px-5 py-4 text-slate-600">{(p.field_profile || {}).field_job_title}</td><td className="px-5 py-4"><span className="text-amber-600 text-xs">Pending</span></td><td className="px-5 py-4"></td>
              </tr>
            ))}
            {data.employees.length === 0 && data.pending.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">No field employees yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invite-field-dialog">
          <DialogHeader><DialogTitle>Add Field Employee</DialogTitle><DialogDescription>They'll get a secure email link to set a password and access the mobile plumbing workspace.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="field-name-input" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="field-email-input" /></div>
            <div><Label>Job title</Label>
              <Select value={form.job_title} onValueChange={(v) => setForm({ ...form, job_title: v })}>
                <SelectTrigger data-testid="field-title-select"><SelectValue /></SelectTrigger>
                <SelectContent>{titles.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Employee number</Label><Input value={form.employee_number} onChange={(e) => setForm({ ...form, employee_number: e.target.value })} /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            <div><Label>Team</Label>
              <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                <SelectTrigger data-testid="field-team-select"><SelectValue placeholder="No team" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No team</SelectItem>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="submit-field-btn">Send Invitation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
